import React, { useState, useEffect, useCallback } from 'react';
import { ClipboardList, X, Loader, Lock } from 'lucide-react';
import { DETAIL_FIELDS } from '../utils/driverDetailFields';

// ── Driver self-service detail form ───────────────────────────────────────────
// Shows in the portal when an admin has requested the driver complete missing
// personal details (ABN, bank, contact…). On submit it writes the values to the
// Drivers sheet via the existing updateDriver action, then marks the request done.

const DETAILS_URL = '/.netlify/functions/driver-details-sb';
const APPSSCRIPT_URL = '/.netlify/functions/appsscript';

export default function DriverDetailsCard({ shift, onCountChange }) {
  const driverLogin = (shift?.driver || '').toUpperCase();
  const [request, setRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  const report = useCallback((r) => { if (onCountChange) onCountChange(r ? 1 : 0); }, [onCountChange]);

  const load = useCallback(async () => {
    try {
      const d = await fetch(DETAILS_URL, { method: 'POST', body: JSON.stringify({ action: 'getMyRequest' }) }).then(r => r.json());
      if (d.success) { setRequest(d.request || null); report(d.request); }
    } catch { /* portal must not break on this */ }
  }, [report]);

  useEffect(() => { if (driverLogin) load(); }, [driverLogin, load]);

  const fields = (request?.fields || []).map(k => DETAIL_FIELDS.find(f => f.key === k)).filter(Boolean);

  const submit = async () => {
    // Require every requested field
    const missing = fields.filter(f => !((values[f.key] || '').toString().trim()));
    if (missing.length) { alert(`Please complete: ${missing.map(f => f.label).join(', ')}`); return; }
    setBusy(true);
    try {
      const updates = {};
      fields.forEach(f => { updates[f.key] = (values[f.key] || '').toString().trim(); });
      const w = await fetch(APPSSCRIPT_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'updateDriver', login: driverLogin, updates }),
      }).then(r => r.json());
      if (!w.success) { alert(`Could not save: ${w.error || 'unknown error'}`); setBusy(false); return; }
      await fetch(DETAILS_URL, { method: 'POST', body: JSON.stringify({ action: 'completeRequest' }) });
      setRequest(null); report(null); setOpen(false);
    } catch (e) {
      alert(`Error: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  if (!request) return null;

  const inp = { width: '100%', padding: '11px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.95rem', boxSizing: 'border-box' };

  return (
    <div style={{ width: '100%', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 12, background: 'rgba(239,68,68,0.08)', border: '1px solid var(--hp-red)' }}>
        <ClipboardList size={22} color="var(--hp-red)" style={{ flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Action needed: complete your details</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
            {request.note || 'We need a few details to finish setting you up.'}
          </div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>Complete now</button>
      </div>

      {open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1200, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '24px 12px', overflowY: 'auto' }}>
          <div style={{ width: 520, maxWidth: '100%', background: 'var(--hp-card)', color: 'var(--hp-text)', borderRadius: 14, border: '1px solid var(--hp-border)', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', borderBottom: '1px solid var(--hp-border)' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>Your details</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}><X size={16} /></button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {fields.map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', display: 'block', marginBottom: 5 }}>{f.label}</label>
                  <input
                    style={inp} type={f.type || 'text'} placeholder={f.placeholder || ''}
                    value={values[f.key] || ''}
                    onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
                  />
                </div>
              ))}
              <p style={{ fontSize: '0.72rem', color: 'var(--hp-muted)', margin: '2px 0 0' }}>
                These go straight to the office. Your bank details are used only for paying your invoices.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, padding: '16px 20px', borderTop: '1px solid var(--hp-border)' }}>
              <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={busy} onClick={submit}>
                {busy ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> : <><Lock size={14} /> Submit details</>}
              </button>
              <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
